import { el } from '../../lib/dom';

/**
 * Zavihki znotraj zaslona — DS `.tabs` / `.tab` (`components.css:492`).
 *
 * **Vizualno je vse DS, vzorec je naš**, ker DS zanj nima modula JS (v `js/`
 * so `theme`, `a11y`, `menu`, `overlays` in `cmb`). Zgrajen je po APG:
 * `role="tablist"`, `role="tab"`, `role="tabpanel"`, roving `tabindex` in
 * **samodejna izbira** — panel se zamenja takoj ob premiku fokusa, ker
 * menjava ne stane ničesar in dvojni pritisk (izberi, nato potrdi) bi bil
 * tipkovničnemu uporabniku samo davek.
 *
 * ⚠️ **Zavihek ni navigacija.** Spodnja vrstica (`.ao-tabbar`) menja **zaslon**
 * in ima svoj naslov v poti; ti zavihki menjajo samo del enega zaslona in
 * naslova ne spreminjajo. Zato tudi ne gredo v `location.hash`.
 *
 * 🔑 **`←` in `→` sta tu last seznama zavihkov, ne predvajalnika.** Globalna
 * bližnjica za menjavo postaje se ob fokusu v `[role="tablist"]` umakne — isto
 * načelo kot presledek na gumbu ([`domain/shortcuts.ts`](../../domain/shortcuts.ts)).
 *
 * ✅ **Zavihek je `<button>` — od DS v4.26.2 ([D2 §48](../../../docs/04-delivery/D2-ds-requests.md)).**
 * Do tedaj je moral biti `<span class="tab">`, ker DS gumba nikjer ni ponastavil
 * in bi `.tab` na `<button>` obdržal sivo ozadje, obrobo in pisavo brskalnika.
 * v4.26.2 je v `.tab` dodal `appearance/background/border/font-family` reset,
 * zato je gumb zdaj vizualno enak span-u — in `<button role="tab">` je pravi
 * interaktivni element, ne `<span>` z ročno prilepljeno vlogo.
 *
 * ⚠️ **Prehod na `<button>` NE izbriše ne roving `tabindex` ne rokovalnika za
 * `Enter`/presledek** — zahtevek §48 je to napačno napovedal. Roving `tabindex`
 * (0/-1) je **APG zahteva za `tablist`**, ne posledica span-a: v tab zaporedju
 * mora biti natanko en zavihek. Rokovalnik za presledek pa mora ostati, ker je
 * **presledek globalna bližnjica** (predvajaj/ustavi, P2 §8.8) in mora
 * `preventDefault`, sicer bi ob premiku po zavihkih ustavil zvok. Pridobitev je
 * torej semantika (native gumb), ne krajša koda.
 */

export interface TabItem {
  /** Stabilna oznaka; iz nje nastaneta `id` gumba in panela. */
  id: string;
  /** Vsebina panela. Zavihki je ne ustvarjajo in je ne posodabljajo. */
  content: Node[];
}

export interface Tabs {
  /** `.tabs` — vrstica z gumbi. Vstavi jo klicno mesto, nad panele. */
  list: HTMLDivElement;
  /** Paneli v vrstnem redu postavk; klicno mesto jih vstavi za `list`. */
  panels: HTMLDivElement[];
  /** Napis zavihka. Pride od zunaj, ker se prevaja ob vsakem `update()`. */
  setLabel(index: number, label: string): void;
  /** Dostopno ime seznama zavihkov (`aria-label`). */
  setListLabel(label: string): void;
}

export function createTabs(prefix: string, items: TabItem[]): Tabs {
  let active = 0;

  const buttons = items.map((item, index) =>
    el('button', {
      class: 'tab',
      type: 'button',
      role: 'tab',
      id: `${prefix}Tab-${item.id}`,
      'aria-controls': `${prefix}Panel-${item.id}`,
      'aria-selected': 'false',
      // Roving tabindex: v zaporedju tabulatorja je natanko en zavihek, sicer
      // bi se moral uporabnik skozi vse prebiti, preden pride do vsebine.
      // Ostaja tudi na `<button>` — je APG zahteva za `tablist`, ne posledica
      // prejšnjega `<span>`.
      tabindex: index === 0 ? '0' : '-1',
    }),
  );

  /**
   * Panel **nima `tabindex`**, ker vsak od njih vsebuje fokusabilen element
   * (gumb, drsnik, `summary`). APG ga zahteva le, kadar panel nima nobenega —
   * takrat bi bila vsebina s tipkovnico nedosegljiva.
   */
  const panels = items.map((item) =>
    el(
      'div',
      {
        class: 'nw-tabpanel',
        role: 'tabpanel',
        id: `${prefix}Panel-${item.id}`,
        'aria-labelledby': `${prefix}Tab-${item.id}`,
      },
      item.content,
    ),
  );

  function select(index: number, moveFocus: boolean): void {
    active = index;
    buttons.forEach((button, i) => {
      const on = i === index;
      button.classList.toggle('active', on);
      button.setAttribute('aria-selected', String(on));
      button.setAttribute('tabindex', on ? '0' : '-1');
    });
    for (const [i, panel] of panels.entries()) panel.toggleAttribute('hidden', i !== index);
    if (moveFocus) buttons[index]?.focus();
  }

  for (const [index, button] of buttons.entries()) {
    button.addEventListener('click', () => {
      select(index, false);
    });
  }

  const list = el('div', { class: 'tabs', role: 'tablist' }, buttons);

  list.addEventListener('keydown', (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    // Modifikator pripada sistemu — `Alt+←` je »nazaj« v brskalniku.
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const last = items.length - 1;
    let next: number;
    switch (event.key) {
      /**
       * `Enter` in presledek (APG). Ob samodejni izbiri je zavihek ob fokusu že
       * izbran, zato tu ni česa spremeniti — pomembno je, da tipki **ne padeta
       * naprej**: presledek je globalno predvajaj in ustavi (P2 §8.8) in bi zvok
       * ustavil ob premiku po zavihkih.
       *
       * ⚠️ `preventDefault` spodaj hkrati prepreči **native klik gumba** — sicer
       * bi `<button>` ob presledku/Enter sprožil še `click` in `select` bi tekel
       * dvakrat. Miškin klik gre po svoji poti (`click` poslušalec).
       */
      case 'Enter':
      case ' ':
        next = active;
        break;
      // Ovijanje je namerno: pri štirih zavihkih je pot od zadnjega do prvega
      // en pritisk in ne štirje.
      case 'ArrowLeft':
        next = active === 0 ? last : active - 1;
        break;
      case 'ArrowRight':
        next = active === last ? 0 : active + 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    // Brez tega `←` `→` zadrsata vrstico zavihkov, `Home` in `End` pa skočita
    // na vrh in dno strani — hkrati z menjavo, ki jo je uporabnik zahteval.
    event.preventDefault();
    select(next, true);
  });

  select(0, false);

  return {
    list,
    panels,
    setLabel(index, label) {
      const button = buttons[index];
      if (button !== undefined) button.textContent = label;
    },
    setListLabel(label) {
      list.setAttribute('aria-label', label);
    },
  };
}
